/**
 * BYOK provider catalog. Single source of truth for the dropdown, the form,
 * and the help-link sidebar. Adding a new provider is a one-line job here
 * plus a case in the edge function's `buildProviderCall`.
 */
import type { Database } from "@/integrations/supabase/types";

export type ByokProvider = Database["public"]["Enums"]["byok_provider"] | "lovable";

export interface ProviderInfo {
  id: ByokProvider;
  /** Display name in the dropdown. */
  label: string;
  /** Where the user goes to find / mint a key. Empty for "lovable". */
  helpUrl?: string;
  /** Short reassurance copy under the dropdown. */
  blurb: string;
  /** Whether the user must pick a specific model (vs. accept the default). */
  requiresModelInput?: boolean;
}

export const PROVIDERS: readonly ProviderInfo[] = [
  {
    id: "lovable",
    label: "Lovable AI (default)",
    blurb: "Uses the included Lovable AI gateway. No key required.",
  },
  {
    id: "openai",
    label: "OpenAI",
    helpUrl: "https://platform.openai.com/api-keys",
    blurb: "GPT-4o, o-series. Default model: gpt-4o-mini.",
  },
  {
    id: "anthropic",
    label: "Anthropic",
    helpUrl: "https://console.anthropic.com/settings/keys",
    blurb: "Claude 3.5 Sonnet by default.",
  },
  {
    id: "google",
    label: "Google Gemini",
    helpUrl: "https://aistudio.google.com/app/apikey",
    blurb: "Gemini 1.5 Flash by default.",
  },
  {
    id: "mistral",
    label: "Mistral",
    helpUrl: "https://console.mistral.ai/api-keys",
    blurb: "Mistral Large by default.",
  },
  {
    id: "groq",
    label: "Groq",
    helpUrl: "https://console.groq.com/keys",
    blurb: "Llama 3.3 70B Versatile by default — very fast inference.",
  },
  {
    id: "openrouter",
    label: "OpenRouter",
    helpUrl: "https://openrouter.ai/keys",
    blurb: "Pick any model from the OpenRouter catalog.",
    requiresModelInput: true,
  },
] as const;

/** For the "store key" form — excludes lovable (no key needed). */
export const STORABLE_PROVIDERS = PROVIDERS.filter((p) => p.id !== "lovable");

export function getProviderInfo(id: ByokProvider): ProviderInfo {
  return PROVIDERS.find((p) => p.id === id) ?? PROVIDERS[0];
}
